import { FC, useMemo, useRef, useState } from 'react'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/chevron.svg'
import useOutsideClick from 'src/hooks/useOutsideClick'
import classes from './operatorFilterDropdown.module.scss'

export interface OperatorFilterOption {
  count?: number
  id: string
  name: string
}

interface OperatorFilterDropdownProps {
  className?: string
  onChange: (next: string[] | null) => void
  operators: OperatorFilterOption[]
  // null = all operators selected; [] = none; [ids] = subset.
  selected: string[] | null
}

const CheckIcon: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <path
      d="M3.5 8.2L6.5 11L12.5 5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
)

export const OperatorFilterDropdown: FC<OperatorFilterDropdownProps> = ({
  className,
  onChange,
  operators,
  selected,
}) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)

  useOutsideClick({ onOutsideClick: () => setOpen(false), ref })

  const normalizedSearch = search.trim().toLowerCase()
  const visibleOptions = useMemo(
    () =>
      normalizedSearch
        ? operators.filter((o) =>
            (o.name || '').toLowerCase().includes(normalizedSearch)
          )
        : operators,
    [operators, normalizedSearch]
  )

  const selectedCount = selected === null ? operators.length : selected.length
  const valueLabel =
    selected === null
      ? 'All Operators Selected'
      : selectedCount === 0
        ? 'No Operators Selected'
        : `${selectedCount} Operator${selectedCount === 1 ? '' : 's'} Selected`

  const allSelected = selected === null
  const isSelected = (id: string) => selected === null || selected.includes(id)

  const toggle = (id: string) => {
    const current = selected === null ? operators.map((o) => o.id) : selected
    const next = current.includes(id)
      ? current.filter((operatorId) => operatorId !== id)
      : [...current, id]
    onChange(
      next.length === operators.length && operators.length > 0 ? null : next
    )
  }

  return (
    <div className={`${classes.operatorDropdown} ${className ?? ''}`} ref={ref}>
      <button
        className={`${classes.operatorDropdownTrigger} ${
          open ? classes.open : ''
        }`}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className={classes.operatorDropdownLabel}>Operator</span>
        <span className={classes.operatorDropdownValue}>
          {valueLabel.startsWith('All ') ? (
            <>
              <span className={classes.filterValueAccent}>All</span>
              {valueLabel.replace(/^All\s+/, '')}
            </>
          ) : (
            valueLabel
          )}
          <ChevronIcon />
        </span>
      </button>

      {open && (
        <div className={classes.operatorDropdownPanel}>
          <div className={classes.operatorDropdownSearch}>
            <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
              <circle
                cx="7"
                cy="7"
                r="5"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M10.75 10.75L14 14"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.5"
              />
            </svg>
            <input
              autoFocus
              className={classes.operatorDropdownSearchInput}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Operator"
              type="text"
              value={search}
            />
            <button
              className={classes.operatorDropdownSearchValue}
              onClick={() => setOpen(false)}
              type="button"
            >
              <span className={classes.filterValueAccent}>All</span>
              Operators Selected
              <ChevronIcon />
            </button>
          </div>

          <button
            className={classes.operatorSelectAll}
            onClick={() => onChange(allSelected ? [] : null)}
            type="button"
          >
            <span
              className={`${classes.operatorCheckbox} ${
                allSelected ? classes.checked : ''
              }`}
            >
              {allSelected && <CheckIcon />}
            </span>
            Select All Operators
          </button>

          <div className={classes.operatorPanelSectionTitle}>Operators</div>

          <div className={classes.operatorPanelList}>
            {visibleOptions.length === 0 && (
              <div className={classes.operatorPanelEmpty}>
                No operators found
              </div>
            )}
            {visibleOptions.map((operator) => (
              <button
                className={classes.operatorPanelOption}
                key={operator.id}
                onClick={() => toggle(operator.id)}
                type="button"
              >
                <span
                  className={`${classes.operatorCheckbox} ${
                    isSelected(operator.id) ? classes.checked : ''
                  }`}
                >
                  {isSelected(operator.id) && <CheckIcon />}
                </span>
                <span className={classes.operatorPanelName}>
                  {operator.name}
                </span>
                {operator.count !== undefined && (
                  <span className={classes.operatorPanelCount}>
                    · {operator.count.toLocaleString()} Chats
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
